import React from 'react';
import { uuidToString } from './UUID';
import { LaneDisplayInterface } from './PrimaryDisplay';
import { AltAttributionUtil } from './InvestigationDisplay';
import { Config } from './Config';
import { checkSnoozeState } from './SnoozeIsotopeDisplay';
import { AuthenticationInterface } from './AuthenticationDisplay';

import { withTranslation } from 'react-i18next';
import { t } from 'i18next';
import './i18n';
import 'react-hammerjs';
import * as messages from '@radnuc/messages';
import element_names from './element-names'
import { element_name } from './element-names'
import mute_icon from './img/mute-32-lightgray.png'
import unmute_icon from './img/unmute-32-lightgray.png'
import mute_this_one_icon from './img/mute-single-32-lightgray.png'
import alert_wav from './audio/Alert.wav'
import acknowledge_wav from './audio/Chime.wav'
import i18n from './i18n';
import 'react-hammerjs';


type Investigation = messages.Encounter.Encounter | messages.Rad.Investigation;
type PCSResult = messages.Rad.PCSResult;
type NeutronResult = messages.Rad.NeutronResult;

interface AudioAlertProps {
    investigations: Investigation[],
    investigationsNotAcknowledged?: Investigation[],
    currentPCSResult: PCSResult | null,
    currentNeutronResult: NeutronResult | null,
    hidden?: boolean,
    laneToAlarm?: number,
    isSecondary?: boolean,
    convertLaneToDirection?: boolean,
    laneDisplayInterface?: LaneDisplayInterface,
    useSourceHeadingAndSourceLanePositionForDirection?: boolean,
    useTTSVoice?: string,
    config?: Config,
    authenticationInterface?: AuthenticationInterface,
    disableMuteForOne?: boolean,
}
interface AudioAlertState {
    mute: boolean,
    muteThisID: boolean,
}

export class AudioAlertDisplay extends React.Component<AudioAlertProps, AudioAlertState> {
    display_element: React.RefObject<HTMLDivElement>;
    alarmed: boolean = false;
    investigation_id: String | null  = null;
    mute_this_investigation_id: String | null  = null;
    dominant_isotope: String | null = null;
    heading_to_source: number | null = null;
    source_lane_position: number | null = null;
    neutron_detected: boolean = false;
    neutron_heading_to_source: number | null = null;
    acknowledge_timer:NodeJS.Timer | null = null;
    audioClip: HTMLAudioElement|null = null;
    prev_investigation: Investigation | null = null;
    acknowledge_audioClip: HTMLAudioElement|null = null;
    alarmed_map:Map<number,boolean> = new Map<number,boolean>();
    hammerManager: HammerManager | undefined;

    constructor(props: AudioAlertProps) {
        super(props)
        this.state = {
            mute:false,
            muteThisID: false,
        }
        this.display_element = React.createRef<HTMLDivElement>();
        this.handleMuteButtonClick = this.handleMuteButtonClick.bind(this);
        this.handleMuteThisIDButtonClick = this.handleMuteThisIDButtonClick.bind(this);
        this.speakAlert = this.speakAlert.bind(this);
    }

    componentDidMount(){
        if(this.display_element.current !== null){
            this.hammerManager = new Hammer(this.display_element.current)
            this.hammerManager.add(new Hammer.Tap({event : 'tap'}));

            if(this.props.disableMuteForOne){
                //Mute for one disabled
                this.hammerManager.on('tap', this.handleMuteButtonClick.bind(this));
            } else {
                //Mute for one enabled
                this.hammerManager.on('tap', this.handleMuteThisIDButtonClick.bind(this));
                this.hammerManager.add(new Hammer.Press({event : 'press', pointers : 1, time : 650}));
                this.hammerManager.on('press', this.handleMuteButtonClick.bind(this));
            }
        }
    }
    componentWillUnmount(){
        if(this.hammerManager !== undefined)
            this.hammerManager.destroy();
    }
    componentDidUpdate(prevProps: AudioAlertProps) {
        this.findDominantIsotope();
        this.updateNeutronStatus();
        if (this.display_element.current !== null && this.props.investigations.length > 0){
            if(this.props.investigations[0] !== prevProps.investigations[0]) {
                //console.log('Investigation updated');
                //Update for possible audio if it is not finalized (current)
                if(!this.props.investigations[0].finalized || (new Date().getTime() - this.props.investigations[0].end_time < 5000 )){
                    if(AltAttributionUtil.isAltAttrInThisLane(this.props.investigations[0])){
                        //Say dominant threat from investgation
                        this.update(this.props.investigations[0],true);
                    } else {
                        //Say dominant threat from pcs message (default)
                        this.update(this.props.investigations[0]);
                    }
                }
                this.prev_investigation = this.props.investigations[0];
            } else {
                //There are still unfinalized alerts
                const invNotFinalized = this.props.investigations?.filter((inv: Investigation) => {
                    return !inv.finalized
                });
                if(invNotFinalized.length > 0){
                    if(!invNotFinalized[0].finalized || (new Date().getTime() - invNotFinalized[0].end_time < 5000 )){
                        if(AltAttributionUtil.isAltAttrInThisLane(invNotFinalized[0])){
                            //Say dominant threat from investgation
                            this.update(invNotFinalized[0],true);
                        } else {
                            //Say dominant threat from pcs message (default)
                            this.update(invNotFinalized[0]);
                        }
                    }
                    this.prev_investigation = invNotFinalized[0];
                }
            }
        }
        //Acknowledge Alert
        if(this.props.investigationsNotAcknowledged !== undefined){
            if(this.props.investigationsNotAcknowledged !== prevProps.investigationsNotAcknowledged){

                const num_finalized = this.props.investigationsNotAcknowledged?.filter((inv: Investigation) => {
                    return inv.finalized
                }).length;

                //Play acknowledge alert sound only if no active alert
                if(num_finalized !== undefined &&
                    num_finalized === this.props.investigationsNotAcknowledged.length &&
                    this.props.investigationsNotAcknowledged.length > 0 && !window.speechSynthesis.speaking) {
                    if(this.acknowledge_timer == null){
                        this.mute_this_investigation_id = null;
                        this.setState({
                            muteThisID: false,
                        })
                        this.acknowledge_timer = setInterval(() =>{
                            if(!this.state.mute){
                                this.acknowledge_audioClip = new Audio(acknowledge_wav);
                                this.acknowledge_audioClip.volume = 1.0;
                                this.acknowledge_audioClip.play();
                            }
                        },5000);
                    }
                } else {
                    if(this.acknowledge_timer !== null){
                        clearInterval(this.acknowledge_timer);
                        this.acknowledge_timer = null;
                        this.acknowledge_audioClip = null;
                    }
                }
            }
        }
    }

    handleMuteButtonClick() {
        //event.stopPropagation();
        if(this.props.authenticationInterface && this.props.disableMuteForOne !== true)
            this.props.authenticationInterface.authenticate(()=>this.muteButtonClickFunction());
        else
            this.muteButtonClickFunction();

    }
    muteButtonClickFunction(){
        console.log("full mute longpress")

        if(this.state.mute){
            //Unmute
            this.mute_this_investigation_id = null;
            this.setState({
                muteThisID: false,
                mute: false,
            })
        } else {
            this.setState({
                mute: true,
            })
        }
    }

    handleMuteThisIDButtonClick() {
        if(this.mute_this_investigation_id !== null){
            this.mute_this_investigation_id = null;
            if(this.state.mute){
                this.mute_this_investigation_id = null;
                this.setState({
                    muteThisID: false,
                    mute: false,
                })
            } else {
                this.setState({
                    muteThisID: false,
                })
            }
        } else {
            const num_finalized_all = this.props.investigations?.filter((inv: Investigation) => {
                return inv.finalized
            }).length;
            if (num_finalized_all !== this.props.investigations.length){
                this.mute_this_investigation_id = this.investigation_id;
                if(this.state.mute){
                    this.mute_this_investigation_id = null;
                    this.setState({
                        muteThisID: false,
                        mute: false,
                    })
                } else {
                    this.setState({
                        muteThisID: true,
                    })
                }
            } else {
                if(this.state.mute){
                    //Full unmute
                    this.mute_this_investigation_id = null;
                    this.setState({
                        muteThisID: false,
                        mute: false,
                    })
                }
            }
        }
    }

    //**************************************************************************
    //Audio support functions
    findDominantIsotope(){
        let max_isotope = 0;
        let dominant_isotope = null;
        let heading_to_source = null;
        let pcs_source_lane = null;
        let source_lane_position = null;

        if(this.props.currentPCSResult != null){
            this.props.currentPCSResult.pcs_result.forEach((isotope)=>{
                if (isotope.pcs_confidence > max_isotope && isotope.alarm) {
                    max_isotope = isotope.pcs_confidence;
                    dominant_isotope = isotope.name;
                    heading_to_source = this.props.currentPCSResult!.pcs_alpha_source_bearing;
                    source_lane_position = this.props.currentPCSResult!.source_lane_position;
                }
            });
            //Optional Snooze Isotope
            if(this.props.config){
                if(checkSnoozeState(this.props.currentPCSResult,this.props.config)){
                    max_isotope = 0;
                    dominant_isotope = null;
                    heading_to_source = null;
                    pcs_source_lane = null;
                    source_lane_position = null;
                }
            }
        }

        this.dominant_isotope = dominant_isotope;
        this.heading_to_source = heading_to_source;
        this.source_lane_position = source_lane_position;
    }

    updateNeutronStatus(){
        if(this.props.currentNeutronResult != null){
            if(this.props.currentNeutronResult.alarm != undefined){
                this.neutron_detected = this.props.currentNeutronResult.alarm;
                this.neutron_heading_to_source = this.props.currentNeutronResult.neutron_source_bearing;
            } else {
                this.neutron_detected = false;
                this.neutron_heading_to_source = null;
            }
        }
    }

    speakAlert(isotope_name: String | null, heading_or_lane: number | null, report_as_lanes: boolean, convert_lane_as_direction?: boolean, force_speak?: boolean){
        var msg = new SpeechSynthesisUtterance();

        if(this.props.useTTSVoice && this.props.useTTSVoice.length > 0){
            const voices = speechSynthesis.getVoices();
            if(voices.length > 0){
                let voice_idx = voices.findIndex((voice,index)=>{
                    if(voice.name.includes(this.props.useTTSVoice!)){
                        return index;
                    }
                });
                if(voice_idx < 0)
                    voice_idx = 0;
                msg.voice = voices[voice_idx];
            }
        }

        msg.volume = 1.0; //highest volume
        msg.text = this.isotope_as_phonetic(isotope_name);
        if(heading_or_lane !== null){
            if(report_as_lanes){
                if(msg.text === 'Source'){
                    msg.text += ' detected'
                }
                if(convert_lane_as_direction === true){
                    //msg.text += ' '+this.source_lane_as_direction_phonetic(heading_or_lane);
                    const direction_string = 'S '+this.source_lane_as_direction_phonetic(heading_or_lane);
                    msg.text = t(direction_string,{source:msg.text,ns:'trans'})
                } else {
                    msg.text += ', lane '+heading_or_lane;
                }
                if(AltAttributionUtil.isAltAttrInThisLane(this.props.investigations[0])){
                    //add alternate attribution text if it exists
                    msg.text += ',   ,'+this.isotope_as_phonetic(AltAttributionUtil.getAltAttrIsotopeName(this.props.investigations[0]))+
                                ', lane '+AltAttributionUtil.getAltAttrSourceLaneLabel(this.props.investigations[0],this.props.laneDisplayInterface);
                }
            } else {
                //PERM-mobile PERM-M
                if(!msg.text.includes('encounter')){
                    //msg.text += ' '+this.source_direction_as_phonetic(heading_or_lane);
                    const direction_string = 'S '+this.source_direction_as_phonetic(heading_or_lane);
                    msg.text = t(direction_string,{source:msg.text,ns:'trans'})
                }
            }
        } else {
            if(report_as_lanes){
                if(msg.text === 'Source'){
                    msg.text += ' detected'
                }
                if(convert_lane_as_direction === true){
                    //
                } else {
                    msg.text += ', lane unknown';
                }
            } else {
                if(convert_lane_as_direction){
                    //PERM-M
                    if(msg.text === 'Source'){
                        //Translate 'source'
                        msg.text = t('Source',{ns:'trans'});
                    }
                    if(this.source_lane_position === 0){
                        //msg.text += ' , bottom position'
                        msg.text = t('S bottom position',{source:msg.text,ns:'trans'})
                    } else if (this.source_lane_position === 1){
                        //msg.text += ' , top position'
                        msg.text = t('S top position',{source:msg.text,ns:'trans'})
                    } else {
                        //msg.text += ' detected'
                        msg.text = t('S detected',{source:msg.text,ns:'trans'})
                    }
                } else {
                    if(!msg.text.includes('encounter')){
                        msg.text += " detected";
                    }
                }
            }
        }

        if(!window.speechSynthesis.speaking){
            window.speechSynthesis.speak(msg);
        } else if (force_speak){
            window.speechSynthesis.cancel();
            window.speechSynthesis.speak(msg);
        }
    }

    translate_isotope_phonetic(element_name:element_name){
        const language = i18n.language;
        let nuclide_name_phonetic;
        switch(language) {
            case 'en':
                nuclide_name_phonetic =  element_name.phonetic;
                break;
            case 'cz':
                nuclide_name_phonetic =  element_name.phonetic_cz;
                break;
            case 'hu':
                nuclide_name_phonetic =  element_name.phonetic_hu;
                break;
            case 'ro':
                nuclide_name_phonetic =  element_name.phonetic_ro;
                break;
            case 'ru':
                nuclide_name_phonetic =  element_name.phonetic_ru;
                break;
            case 'sk':
                nuclide_name_phonetic =  element_name.phonetic_sk;
                break;
            default:
                nuclide_name_phonetic =  element_name.phonetic;
        }
        return nuclide_name_phonetic;
    }

    isotope_as_phonetic(isotope_name:String|null) {
        // isotope_name = "TL_201"
        var nuclide_number = null;
        var isotope_phonetic = 'Source';
        if(isotope_name !== null){
            var res = isotope_name.split("_");
            var nuclide_symbol = res[0];
            nuclide_number = res[1];
            // nuclide
            var nuclide_name_phonetic = null;
            if (nuclide_symbol != null) {
                for (var i = 0; i < element_names.length; i++) {
                    if (nuclide_symbol === element_names[i].symbol) {
                        nuclide_name_phonetic = this.translate_isotope_phonetic(element_names[i]);
                    }
                }
            }

            // number
            var nuclide_number_phonetic = null;
            if (nuclide_number != null) {
                if(i18n.language === 'en'){
                     // three digit case
                    if (nuclide_number.length === 3) {
                        nuclide_number_phonetic = nuclide_number.substr(0, 1) + " ";

                        // middle number is zero
                        if (nuclide_number.substr(1, 1) === "0") {
                            nuclide_number_phonetic = nuclide_number_phonetic + "o " +
                                            nuclide_number.substr(2, 1);
                        } else {
                            nuclide_number_phonetic =
                            nuclide_number_phonetic + nuclide_number.substr(1, 2);
                        }
                    } else {
                        nuclide_number_phonetic = nuclide_number;
                    }
                } else {
                    nuclide_number_phonetic = nuclide_number;
                }
            }

            if (nuclide_name_phonetic != null) {
                isotope_phonetic = nuclide_name_phonetic;
                if (nuclide_number_phonetic != null) {
                    isotope_phonetic = isotope_phonetic + " " + nuclide_number_phonetic;
                }
            }
        }
        return isotope_phonetic;
    }
    source_lane_as_direction_phonetic(lane:number){
        var direction_as_phonetic = "";
        if (lane === 1){ //base 1
            direction_as_phonetic = "left side"
        }
        if (lane === 2){
            direction_as_phonetic = "right side"
        }
        return direction_as_phonetic;
    }


    source_direction_as_phonetic(source_direction:number) {
        var direction_as_phonetic = "";
        if ((source_direction >= 0) && (source_direction < 10)) {
            direction_as_phonetic = "front";
        }
        if ((source_direction >= 10) && (source_direction < 45)) {
            direction_as_phonetic = "front right";
        }
        if ((source_direction >= 45) && (source_direction < 135)) {
            direction_as_phonetic = "right side";
        }
        if ((source_direction >= 135) && (source_direction < 165)) {
            direction_as_phonetic = "right rear";
        }
        if ((source_direction >= 170) && (source_direction < 190)) {
            direction_as_phonetic = "behind";
        }
        if ((source_direction >= 190) && (source_direction < 225)) {
            direction_as_phonetic = "left rear";
        }
        if ((source_direction >= 225) && (source_direction < 315)) {
            direction_as_phonetic = "left side";
        }
        if ((source_direction >= 315) && (source_direction < 345)) {
            direction_as_phonetic = "front left";
        }
        if ((source_direction >= 345) && (source_direction <= 360)) {
            direction_as_phonetic = "front";
        }
        return direction_as_phonetic;
    }

    checkAndSetSpecialRadAlertCases(thisInvestigation: Investigation){
        //SPECIAL ALERT CASE without PCS detection
        if(this.dominant_isotope === null){
            if(thisInvestigation.isotopes.length > 0){
                if(thisInvestigation.isotopes[0].name === "OVERRANGE"){
                    this.dominant_isotope= "OVERRANGE";
                } else if (thisInvestigation.isotopes[0].name === "HIGH_DOSE"){
                    this.dominant_isotope = "HIGH_DOSE";
                }
            }
        }
    }

    update(thisInvestigation:Investigation, sayThreatFromInvestigation?:boolean){

        if(sayThreatFromInvestigation === undefined)
            sayThreatFromInvestigation = false;

        //Lane Specific Alerts
        let alarm_enabled = true;
        if(thisInvestigation.source_lane !== null && this.props.laneToAlarm !== undefined && this.props.laneToAlarm !== thisInvestigation.source_lane)
            alarm_enabled = false;

        //Alternate attribution Alert
        if(thisInvestigation.source_lane !== null && this.props.laneToAlarm !== undefined && AltAttributionUtil.isAltAttrInThisLane(thisInvestigation,this.props.laneToAlarm))
            alarm_enabled = true;

        //No alarm for secondary
        if(this.props.isSecondary)
            alarm_enabled = false;

        //No horn alarm if manual investigation
        if(thisInvestigation.user_generated){
            this.alarmed = true;
        }

        //check if the investigation is new
        const thisInvestigationId = uuidToString(thisInvestigation.id);
        if(this.investigation_id !== null){
            if(!this.investigation_id.match(thisInvestigationId)){
                if('type' in thisInvestigation){
                    //Encounter message
                    if(thisInvestigation.type === messages.Encounter.EncounterType.RAD){
                        //Check if any detected isotopes
                        if(thisInvestigation.isotopes.length > 0){
                            //New RAD Investigation
                            this.investigation_id = thisInvestigationId;
                            this.alarmed = false;
                            //console.log("New Investigation Not alarmed yet)");
                        }
                    } else if (thisInvestigation.type === messages.Encounter.EncounterType.CHEM){
                        //New CHEM Investigation
                        this.investigation_id = thisInvestigationId;
                        this.alarmed = false;
                    } else if (thisInvestigation.type === messages.Encounter.EncounterType.BIO){
                        //New BIO Investigation
                        this.investigation_id = thisInvestigationId;
                        this.alarmed = false;
                    } else if (thisInvestigation.type === messages.Encounter.EncounterType.RN_FUSION){
                        //New RN Fusion Investigation
                        this.investigation_id = thisInvestigationId;
                        this.alarmed = false;
                    } else if (thisInvestigation.type === messages.Encounter.EncounterType.BIOSIGMA){
                        //New BIOSIGMA Investigation
                        this.investigation_id = thisInvestigationId;
                        this.alarmed = false;
                    }
                } else {
                    //Investigation message (RAD ONLY)
                    //Check if any detected isotopes
                    if(thisInvestigation.isotopes.length > 0){
                        //New Investigation
                        this.investigation_id = thisInvestigationId;
                        this.alarmed = false;
                        //console.log("New Investigation Not alarmed yet)");

                        //Clear alarmed_map
                        this.alarmed_map.clear();
                    }
                }
            } else {
                //Same Investigation
                if(this.props.laneToAlarm !== undefined){
                    //This is a single lane case
                    if(this.alarmed_map.has(this.props.laneToAlarm)){
                        this.alarmed = true;
                    }
                } else {
                    this.alarmed = true;
                }
            }

            if(this.props.laneToAlarm !== undefined){
                //Single lane View case
                if(thisInvestigation.source_lane === this.props.laneToAlarm){
                    //Set this lane as alarmed
                    this.alarmed_map.set(this.props.laneToAlarm,true);
                }
                if(!('type' in thisInvestigation)){
                    //Check alternate Attribution
                    if(thisInvestigation.alternative_attributions !== null && thisInvestigation.alternative_attributions.length > 0){
                        //current alternate attribution exists

                        if(this.prev_investigation === null || !('type' in this.prev_investigation) && (this.prev_investigation.alternative_attributions === null ||
                            (this.prev_investigation.alternative_attributions !== null && this.prev_investigation.alternative_attributions.length === 0))){
                            //Previous investigation did not contain any alternate attribution
                            if(!this.alarmed_map.has(this.props.laneToAlarm)){
                                //This lane has not been alarmed
                                if(thisInvestigation.alternative_attributions[0].source_lane === this.props.laneToAlarm){
                                    this.alarmed = false;
                                    this.alarmed_map.set(this.props.laneToAlarm,true);
                                }
                            }
                        } else if (this.prev_investigation !== null && !('type' in this.prev_investigation) && this.prev_investigation.alternative_attributions !== null &&
                                   this.prev_investigation.alternative_attributions.length > 0){
                            //Previous investgation contains alternate attribution
                            if(thisInvestigation.alternative_attributions[0].source_lane !== this.prev_investigation?.alternative_attributions[0].source_lane){
                                //Alternate attribution lane changed
                                if(!this.alarmed_map.has(this.props.laneToAlarm)){
                                    //This lane has not been alarmed
                                    if(thisInvestigation.alternative_attributions[0].source_lane === this.props.laneToAlarm){
                                        this.alarmed = false;
                                        this.alarmed_map.set(this.props.laneToAlarm,true);
                                    }
                                }
                            }
                        }
                    }
                }
            }
        } else {
            //New Investigation
            this.investigation_id = thisInvestigationId;
            if(thisInvestigation.user_generated){
                this.alarmed = true;
            } else {
                this.alarmed = false;
                //console.log("New Investigation Not alarmed yet)");
                if(this.props.laneToAlarm !== undefined){
                    //Single lane View case
                    if(thisInvestigation.source_lane === this.props.laneToAlarm){
                        this.alarmed_map.set(this.props.laneToAlarm,true);
                    }
                }
            }
        }

        if(!this.state.mute && alarm_enabled){

            if('type' in thisInvestigation){
                //Encounter message
                if(thisInvestigation.type === messages.Encounter.EncounterType.RAD){
                    this.checkAndSetSpecialRadAlertCases(thisInvestigation);
                } else if (thisInvestigation.type === messages.Encounter.EncounterType.CHEM){
                    this.dominant_isotope = "CHEM";
                    this.heading_to_source = null;
                } else if (thisInvestigation.type === messages.Encounter.EncounterType.BIO){
                    this.dominant_isotope = "BIO";
                    this.heading_to_source = null;
                }
            } else {
                this.checkAndSetSpecialRadAlertCases(thisInvestigation);
            }
            if(sayThreatFromInvestigation === true && thisInvestigation.isotopes.length > 0){
                //Base the current threat to speak on investigation instead of current PCS
                this.dominant_isotope = thisInvestigation.isotopes[0].name
            }

            //Sound alert only if not muted
            if(!this.alarmed || this.dominant_isotope !== null || this.neutron_detected){
                var report_as_lanes = false;

                var this_isotope_name = this.dominant_isotope;
                var this_heading_to_source = this.heading_to_source;
                var this_neutron_detected = this.neutron_detected;
                var this_neutron_heading_to_source = this.neutron_heading_to_source;

                let useSourceHeadingAndSourceLanePositionForDirection:boolean = false;
                if(this.props.useSourceHeadingAndSourceLanePositionForDirection){
                    useSourceHeadingAndSourceLanePositionForDirection = true;
                }

                if(('type' in thisInvestigation && thisInvestigation.type===messages.Encounter.EncounterType.RAD) || !('type' in thisInvestigation)){
                    //GAMMA OVERRANGE
                    if(thisInvestigation.isotopes.length > 0){
                        if(thisInvestigation.isotopes[0].name == "OVERRANGE"){
                            this_isotope_name = "OVERRANGE";
                            if(thisInvestigation.source_lane !== null && !useSourceHeadingAndSourceLanePositionForDirection){
                                report_as_lanes = true;
                                this_heading_to_source = thisInvestigation.source_lane+1;
                                this_neutron_heading_to_source = this_heading_to_source;
                            } else {
                                this_heading_to_source = null;
                                this_neutron_heading_to_source = null;
                            }
                        } else if (thisInvestigation.isotopes[0].name == "HIGH_DOSE"){
                            this_isotope_name = "HIGHDOSE";
                            if(thisInvestigation.source_lane !== null && !useSourceHeadingAndSourceLanePositionForDirection){
                                report_as_lanes = true;
                                this_heading_to_source = thisInvestigation.source_lane+1;
                                this_neutron_heading_to_source = this_heading_to_source;
                            } else {
                                this_heading_to_source = null;
                                this_neutron_heading_to_source = null;
                            }
                        }
                    }
                } else if(('type' in thisInvestigation && thisInvestigation.type===messages.Encounter.EncounterType.CHEM)) {
                    this_isotope_name = "CHEM";
                    this_neutron_detected = false;
                    this_heading_to_source = null;
                    this_neutron_heading_to_source = null;
                } else if(('type' in thisInvestigation && thisInvestigation.type===messages.Encounter.EncounterType.BIO)) {
                    this_isotope_name = "BIO";
                    this_neutron_detected = false;
                    this_heading_to_source = null;
                    this_neutron_heading_to_source = null;
                }
                if(useSourceHeadingAndSourceLanePositionForDirection){
                    //use source heading or source lane position
                    report_as_lanes = false;
                    if(this.source_lane_position !== null){
                        this_heading_to_source = null;
                    }
                } else {
                    if(thisInvestigation.source_lane !== null){
                        report_as_lanes = true;
                        if(this.props.laneDisplayInterface !== undefined){
                            if(this.props.laneDisplayInterface.laneNumberAndLabel !== undefined){
                                const label = this.props.laneDisplayInterface.laneNumberAndLabel[thisInvestigation.source_lane].laneLabel;
                                if(isFinite(Number(label))){
                                    this_heading_to_source = Number(label);
                                } else {
                                    //Lane name unparsable as number
                                    this_heading_to_source = thisInvestigation.source_lane+1;
                                }
                            }
                        } else {
                            this_heading_to_source = thisInvestigation.source_lane+1;
                        }
                        this_neutron_heading_to_source = this_heading_to_source;
                    }
                }

                if(!this.alarmed){
                    //Clear temporary mute
                    this.mute_this_investigation_id = null;
                    this.setState({
                        muteThisID: false,
                    })

                    this.audioClip = new Audio(alert_wav);
                    this.audioClip.volume = 1.0;
                    this.audioClip.addEventListener('ended', () =>{
                        if(this_neutron_detected){
                            this.speakAlert('NEUTRONS', this_neutron_heading_to_source, report_as_lanes,this.props.convertLaneToDirection,true);
                        } else {
                            if(AltAttributionUtil.isAltAttrInThisLane(thisInvestigation)){
                                //Special case where there is alternate attribution, reference investigation instead.
                                this.speakAlert(this.props.investigations[0].isotopes[0].name, this_heading_to_source, report_as_lanes,this.props.convertLaneToDirection,true);
                            } else if(this_isotope_name != null){
                                if(this.mute_this_investigation_id === null || (this.mute_this_investigation_id !== null && this.mute_this_investigation_id !== thisInvestigationId)){
                                    this.speakAlert(this_isotope_name, this_heading_to_source, report_as_lanes,this.props.convertLaneToDirection,true);
                                }
                            }
                        }
                        this.alarmed = true;
                        if(this.audioClip!== null){
                            if(this.audioClip.ended)
                                this.audioClip = null;
                        }
                    });
                    try{
                        //Interrupt all other audio
                        if(window.speechSynthesis.speaking)
                            window.speechSynthesis.cancel();
                        if(this.acknowledge_audioClip !== null)
                            this.acknowledge_audioClip.pause();

                        this.audioClip.play();
                    } catch (err){
                        console.log('Must interact first to enable sound');
                    }
                } else {
                    if(this.audioClip===null){
                        //Already alarmed
                        if(this.neutron_detected){
                            this.speakAlert('NEUTRONS', this_neutron_heading_to_source, report_as_lanes,this.props.convertLaneToDirection,false);
                        } else {
                            if(this.dominant_isotope !== null){
                                if(this.mute_this_investigation_id === null || (this.mute_this_investigation_id !== null && this.mute_this_investigation_id !== thisInvestigationId)){
                                    this.speakAlert(this_isotope_name, this_heading_to_source, report_as_lanes,this.props.convertLaneToDirection,false);
                                }
                            }
                              //this.speakAlert(this_isotope_name, this_heading_to_source, report_as_lanes,this.props.convertLaneToDirection,false);
                        }
                    }
                }
                //console.log('alarmed: '+this.alarmed + ' ID:'+this.dominant_isotope+' heading: ' +this.heading_to_source)
            }
        }
    }

    render() {
        var audioControlButton;
        if(this.props.hidden){

        } else {
            if(this.mute_this_investigation_id !== null){
                if(this.state.muteThisID){
                    audioControlButton = <img src={mute_this_one_icon} alt="MuteOne" style={{height:'26px',paddingBottom:'2px',paddingRight:'5px',paddingLeft:'5px'}}></img>
                } else {
                    audioControlButton = <img src={unmute_icon} alt="Mute" style={{height:'26px',paddingBottom:'2px',paddingRight:'5px',paddingLeft:'5px'}}></img>
                }
            } else {
                if(this.state.mute){
                    audioControlButton = <img src={mute_icon} alt="Mute" style={{height:'26px',paddingBottom:'2px',paddingRight:'5px',paddingLeft:'5px'}}></img>
                } else {
                    audioControlButton = <img src={unmute_icon} alt="Unmute" style={{height:'26px',paddingBottom:'2px',paddingRight:'5px',paddingLeft:'5px'}}></img>
                }
            }
        }
        /*
        <div id="audio-control" style={{position:'relative',  cursor: 'pointer', height:'26px'}}
            onClick={this.handleMuteThisIDButtonClick} ref={this.display_element}>
            {audioControlButton}
        </div>
        */
        /*
        <Hammer onTap={()=>{
            console.log('onTap')
            if(this.mute_this_investigation_id !== null){
                console.log('Clear mute_this_investigation_id')
                this.mute_this_investigation_id = null;
                this.setState({
                    muteThisID: false,
                })
            } else {
                console.log('Set mute_this_investigation_id')
                this.mute_this_investigation_id = this.investigation_id;
                this.setState({
                    muteThisID: true,
                })
            }
        }}>
        <div id="audio-control" style={{position:'relative',  cursor: 'pointer', height:'26px'}}
            ref={this.display_element}>
            {audioControlButton}
        </div>
        </Hammer>
        */
        return (
            <div id="audio-control" style={{position:'relative',  cursor: 'pointer', height:'26px', padding:'10px', right:'-10px'}}
                ref={this.display_element}>
                {audioControlButton}
            </div>

        )

    }
} withTranslation()
export default AudioAlertDisplay
